import { useRef } from 'react';
import { useGridMode } from 'contexts/GridModeContext';
import { useIsActiveTable } from 'contexts/TableActiveContext';

/**
 * Scope an overlay's open flag to the table it was opened on.
 *
 * Two separate things let a modal opened on Table A carry over onto Table B in
 * the single-table carousel:
 *
 * 1. The overlay escapes its own panel. GameModal and TableMenuPanel portal to
 *    <body>; Chat and Table History force `visibility: visible`. Either way the
 *    parked panel's `visibility: hidden` (see RebindingTableView's keep-alive)
 *    no longer hides them, so they keep painting over whichever table the
 *    player swiped to.
 * 2. The open flag itself is app-wide. `openAskBuyin`, `openAskPassword` and
 *    friends live in the GlobalContext mounted at the app root (index.tsx), so
 *    every table's panel reads the same `true` — the buy-in Table A raised
 *    re-renders as Table B's own buy-in modal.
 *
 * The table on screen when the flag rose owns the overlay: only that panel may
 * show it, and only while it is the table the player is on. Swiping away hides
 * it, swiping back to the owner shows it again with its state (a half-typed
 * buy-in amount, a scrolled history) intact.
 *
 * Grid cells are the exception: they DO sit inside a TableActiveProvider, but
 * there it is only the ambient-SOUND gate — only the focused cell may play its
 * table's audio (EN-10577). Interaction scoping deliberately ignores that gate
 * in grid mode and owns every flag on sight, because (a) each grid cell mounts
 * its own GlobalProvider, so the app-wide flags this hook exists to scope are
 * already per-table there; (b) gesture-driven opens would hit a rising-edge
 * ordering trap — the cell's focus handler runs AFTER the inner control's
 * handler, so ownership would latch false on the first tap and stay false for
 * as long as the flag stayed up; and (c) the MTT auto-fold warning must
 * surface from a NON-focused cell, whose Ok button is the only thing that
 * resets the inactivity clock (dev review, PR #3747).
 *
 * Elsewhere outside the carousel — lobby modals, storybook, tests — there is
 * still no TableActiveProvider, so `isActiveTable` is true and every flag is
 * owned on sight: behavior is unchanged.
 */
export const useTableScopedOpen = (open: boolean): boolean => {
  const isActiveTable = useIsActiveTable();
  const gridMode = useGridMode();
  const ownedRef = useRef(false);
  const wasOpenRef = useRef(false);

  // Claim on the rising edge, during render. An effect would settle ownership
  // only after paint — one frame of the other table's modal on screen, which is
  // exactly what this hook exists to prevent. In grid mode every rise is ours
  // (see above), so the refs stay truthful even if this subtree ever left grid
  // mode with the flag still up.
  if (open && !wasOpenRef.current) ownedRef.current = gridMode || isActiveTable;
  wasOpenRef.current = open;

  if (gridMode) return open;
  return open && isActiveTable && ownedRef.current;
};

export default useTableScopedOpen;
